import { AutoAwesome } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import type { GeneratingModel } from '~/utils/transforms/imageDataTransforms';
import { DrawnImage } from '../adapters/types';

/** What the mark reads of a dataset: its name, pair label and origin. */
type MarkedDataset = Pick<DrawnImage, 'label' | 'pairLabel' | 'generatedBy'>;

interface AiGeneratedMarkProps {
    /**
     * The datasets shown together, such as the images a tile draws; the
     * mark names the generated ones.
     */
    readonly images: ReadonlyArray<MarkedDataset>;
    /** Test id of the mark; its card's is this with `-details`. */
    readonly 'data-testid'?: string;
}

/** A dataset the mark names: one with the model that generated it. */
type GeneratedImage = MarkedDataset & {
    readonly generatedBy: GeneratingModel;
};

/**
 * The words a screen reader reads for one generated image, headed by its
 * pair label when the tile blends two, so the listener can tell which image
 * of the pair was generated. Never the image's name, which is PHI.
 */
function spokenOrigin({ pairLabel, generatedBy }: GeneratedImage): string {
    const origin = `${generatedBy.generated} / ${generatedBy.model}`;
    return pairLabel === undefined ? origin : `${pairLabel}: ${origin}`;
}

/** One labelled value of an image's card: a dim key, a readable value. */
function OriginRow({ name, value }: { name: string; value: string }) {
    return (
        <>
            <Box component="span" sx={{ opacity: 0.7 }}>
                {name}
            </Box>
            <Box component="span">{value}</Box>
        </>
    );
}

/**
 * Marks AI-generated data, such as a tile that draws a generated image or
 * a generated structure set: a sparkle whose tooltip is a small card per
 * generated dataset, with the model and its modality mapping. When the tile blends two images, each card is headed by its
 * image's name, masked like the tile title, so the user can tell the pair
 * apart. Draws nothing when every image was acquired.
 */
export default function AiGeneratedMark({
    images,
    'data-testid': testId = 'tile-ai-generated',
}: AiGeneratedMarkProps) {
    const generated = images.filter(
        (image): image is GeneratedImage => image.generatedBy !== undefined
    );
    if (generated.length === 0) {
        return null;
    }
    const named = images.length > 1;
    return (
        // The card describes the mark; the mark keeps its own short name.
        <Tooltip
            describeChild
            placement="bottom-start"
            title={
                <Box
                    data-testid={`${testId}-details`}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        py: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            color: 'primary.light',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        AI Generated
                    </Box>
                    {generated.map((image, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 1fr',
                                columnGap: 1,
                                rowGap: 0.25,
                                fontSize: 12,
                            }}
                        >
                            {named && image.label ? (
                                <Box
                                    sx={{
                                        gridColumn: '1 / -1',
                                        fontWeight: 600,
                                    }}
                                >
                                    <SensitiveInfoTypography>
                                        {image.label}
                                    </SensitiveInfoTypography>
                                </Box>
                            ) : null}
                            <OriginRow
                                name="Model"
                                value={image.generatedBy.model}
                            />
                            {image.generatedBy.modalityHint ? (
                                <OriginRow
                                    name="Modality"
                                    value={image.generatedBy.modalityHint}
                                />
                            ) : null}
                        </Box>
                    ))}
                </Box>
            }
        >
            <Box
                component="span"
                role="img"
                aria-label={generated.map(spokenOrigin).join('. ')}
                data-testid={testId}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    color: 'primary.main',
                }}
            >
                <AutoAwesome sx={{ fontSize: 14 }} />
            </Box>
        </Tooltip>
    );
}
